import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import { GarageDetailResponse } from '../../types';
import type { GarageDocumentResponse } from '../../types/settings';
import FileDropZone from '../../components/onboarding/FileDropZone';
import Layout from '../../components/Layout';
import { useLanguage } from '../../i18n/LanguageContext';

type AuthProp = ReturnType<typeof useAuth>;

const POLL_INTERVAL_MS = 15_000;

const DAY_KEYS = [
  'onboarding.hours.sunday',
  'onboarding.hours.monday',
  'onboarding.hours.tuesday',
  'onboarding.hours.wednesday',
  'onboarding.hours.thursday',
  'onboarding.hours.friday',
  'onboarding.hours.saturday',
];

/** Convert ISO dayOfWeek (1=Mon, 7=Sun) to JS (0=Sun, 6=Sat) */
function isoToJsDow(isoDow: number): number {
  return isoDow === 7 ? 0 : isoDow;
}

interface EditHoursEntry {
  dayOfWeek: number;
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useLanguage();
  const colors: Record<string, { bg: string; color: string }> = {
    PENDING: { bg: '#fef3c7', color: '#92400e' },
    APPROVED: { bg: '#dcfce7', color: '#166534' },
    REJECTED: { bg: '#fef2f2', color: '#991b1b' },
    UPDATE_REQUESTED: { bg: '#dbeafe', color: '#1e40af' },
  };
  const statusKeys: Record<string, string> = {
    PENDING: 'status.pending',
    APPROVED: 'status.approved',
    REJECTED: 'status.rejected',
    UPDATE_REQUESTED: 'status.updateRequested',
    DRAFT: 'status.draft',
  };
  const c = colors[status] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{ display: 'inline-block', padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.8rem', fontWeight: 600, background: c.bg, color: c.color }}>
      {t(statusKeys[status] || 'status.pending')}
    </span>
  );
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

interface EditFormData {
  name: string;
  phone: string;
  email: string;
  city: string;
  area: string;
  streetAddress: string;
  managerName: string;
  numberOfWorkers: string;
  licenseNumber: string;
}

export default function PendingApprovalPage({ auth }: { auth: AuthProp }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [garage, setGarage] = useState<GarageDetailResponse | null>(null);
  const [documents, setDocuments] = useState<GarageDocumentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<EditFormData>({
    name: '', phone: '', email: '', city: '', area: '',
    streetAddress: '', managerName: '', numberOfWorkers: '', licenseNumber: '',
  });
  const [editHours, setEditHours] = useState<EditHoursEntry[]>([]);

  const prevStatusRef = useRef<string | null>(null);

  const fetchGarage = useCallback(async () => {
    if (!auth.token) return;
    try {
      const data = await api.getMyGarage(auth.token);
      setGarage(data);
      // Owner Emirates ID and other docs live in garage_documents (not GarageMedia),
      // so they aren't part of GarageDetailResponse. Fetch separately.
      api.listMyDocuments(auth.token).then(setDocuments).catch(() => { /* non-fatal */ });

      if (data.status === 'APPROVED') {
        navigate('/dashboard', { replace: true });
        return;
      }

      if (data.status === 'UPDATE_REQUESTED' && prevStatusRef.current !== 'UPDATE_REQUESTED') {
        setEditMode(true);
        setForm({
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
          city: data.city || '',
          area: data.area || '',
          streetAddress: data.streetAddress || '',
          managerName: data.managerName || '',
          numberOfWorkers: data.numberOfWorkers?.toString() || '',
          licenseNumber: data.licenseNumber || '',
        });
        setEditHours(data.operatingHours.map((h) => ({
          dayOfWeek: h.dayOfWeek,
          openTime: typeof h.openTime === 'string' ? h.openTime.slice(0, 5) : '08:00',
          closeTime: typeof h.closeTime === 'string' ? h.closeTime.slice(0, 5) : '18:00',
          isClosed: h.isClosed ?? false,
        })));
      }

      prevStatusRef.current = data.status;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pending.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [auth.token, navigate, t]);

  useEffect(() => {
    fetchGarage();
    const interval = setInterval(fetchGarage, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchGarage]);

  const handleInputChange = (field: keyof EditFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateEditDay = (dayOfWeek: number, updates: Partial<EditHoursEntry>) => {
    setEditHours((prev) => prev.map((h) => (h.dayOfWeek === dayOfWeek ? { ...h, ...updates } : h)));
  };

  const handleSubmitForReview = async () => {
    if (!auth.token || !garage) return;
    setSubmitting(true);
    setError(null);
    try {
      const updateData = {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        city: form.city || undefined,
        area: form.area || undefined,
        streetAddress: form.streetAddress || undefined,
        managerName: form.managerName || undefined,
        numberOfWorkers: form.numberOfWorkers ? parseInt(form.numberOfWorkers, 10) : undefined,
        licenseNumber: form.licenseNumber || undefined,
        operatingHours: editHours.map((h) => ({
          dayOfWeek: h.dayOfWeek,
          openTime: h.openTime,
          closeTime: h.closeTime,
          isClosed: h.isClosed,
        })),
      };
      await api.updateMyGarage(updateData, auth.token);
      setEditMode(false);
      setLoading(true);
      await fetchGarage();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('pending.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout auth={auth}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </Layout>
    );
  }

  if (error && !garage) {
    return (
      <Layout auth={auth}>
        <div className="error-message">{error || t('dashboard.noGarage')}</div>
      </Layout>
    );
  }

  if (!garage) return null;

  const isUpdateRequested = garage.status === 'UPDATE_REQUESTED';
  const thumbnail = garage.media.find((m) => m.mediaType === 'THUMBNAIL');

  return (
    <Layout auth={auth} garageStatus={garage.status} garageName={garage.name}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1a1a2e', margin: 0 }}>{t('pending.title')}</h1>
            <p style={{ color: '#6b7280', fontSize: '0.9375rem', marginTop: '0.25rem' }}>
              {isUpdateRequested
                ? t('pending.subtitle.updateRequested')
                : garage.status === 'REJECTED'
                  ? t('pending.subtitle.rejected')
                  : t('pending.subtitle.pending')}
            </p>
          </div>
          <StatusBadge status={garage.status} />
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>
        )}

        {garage.rejectionReason && garage.status === 'REJECTED' && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
            <strong style={{ color: '#991b1b', fontSize: '0.875rem' }}>{t('pending.rejectionReason')}</strong>
            <p style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '0.25rem' }}>{garage.rejectionReason}</p>
            <div style={{ textAlign: 'center', marginTop: '0.75rem' }}>
              <button
                onClick={() => navigate('/onboarding/license-and-id')}
                style={{
                  padding: '0.5rem 1.5rem', background: '#4f46e5', color: 'white',
                  border: 'none', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                {t('pending.reRegister')}
              </button>
            </div>
          </div>
        )}

        {garage.adminUpdateNote && (
          <div style={{
            background: isUpdateRequested ? '#fef3c7' : '#eff6ff',
            border: `1px solid ${isUpdateRequested ? '#fde68a' : '#bfdbfe'}`,
            borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1.5rem',
          }}>
            <strong style={{ color: isUpdateRequested ? '#92400e' : '#1e40af', fontSize: '0.875rem' }}>
              {isUpdateRequested ? t('pending.actionRequired') : t('pending.adminNote')}
            </strong>
            <p style={{ color: isUpdateRequested ? '#92400e' : '#1e40af', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              {garage.adminUpdateNote}
            </p>
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '1rem' }}>{t('pending.timeline')}</h2>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>{t('pending.submitted')}</div>
              <div style={{ fontSize: '0.875rem', color: '#1a1a2e', marginTop: '0.125rem' }}>{formatDate(garage.createdAt)}</div>
            </div>
            {garage.statusChangedAt && garage.statusChangedAt !== garage.createdAt && (
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>{t('pending.lastUpdated')}</div>
                <div style={{ fontSize: '0.875rem', color: '#1a1a2e', marginTop: '0.125rem' }}>{formatDate(garage.statusChangedAt)}</div>
              </div>
            )}
            {garage.termsAcceptedAt && (
              <div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500 }}>{t('pending.termsAccepted')}</div>
                <div style={{ fontSize: '0.875rem', color: '#1a1a2e', marginTop: '0.125rem' }}>{formatDate(garage.termsAcceptedAt)}</div>
              </div>
            )}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '1rem' }}>{t('pending.garageInfo')}</h2>

          {thumbnail && !editMode && (
            <div style={{ marginBottom: '1rem' }}>
              <img
                src={thumbnail.downloadUrl}
                alt="Garage thumbnail"
                style={{ maxWidth: 200, maxHeight: 120, borderRadius: 8, objectFit: 'cover', border: '1px solid #e5e7eb' }}
              />
            </div>
          )}

          {editMode ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <EditField label={`${t('pending.field.name')} *`} value={form.name} onChange={(v) => handleInputChange('name', v)} />
              <EditField label={`${t('pending.field.phone')} *`} value={form.phone} onChange={(v) => handleInputChange('phone', v)} />
              <EditField label={t('pending.field.email')} value={form.email} onChange={(v) => handleInputChange('email', v)} type="email" />
              <EditField label={t('pending.field.licenseNumber')} value={form.licenseNumber} onChange={(v) => handleInputChange('licenseNumber', v)} />
              <EditField label={t('pending.field.city')} value={form.city} onChange={(v) => handleInputChange('city', v)} />
              <EditField label={t('pending.field.area')} value={form.area} onChange={(v) => handleInputChange('area', v)} />
              <EditField label={t('pending.field.street')} value={form.streetAddress} onChange={(v) => handleInputChange('streetAddress', v)} />
              <EditField label={t('pending.field.manager')} value={form.managerName} onChange={(v) => handleInputChange('managerName', v)} />
              <EditField label={t('pending.field.workers')} value={form.numberOfWorkers} onChange={(v) => handleInputChange('numberOfWorkers', v)} type="number" />
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <InfoField label={t('pending.field.name')} value={garage.name} />
              <InfoField label={t('pending.field.phone')} value={garage.phone} />
              <InfoField label={t('pending.field.email')} value={garage.email} />
              <InfoField label={t('pending.field.licenseNumber')} value={garage.licenseNumber} />
              <InfoField label={t('pending.field.city')} value={garage.city} />
              <InfoField label={t('pending.field.area')} value={garage.area} />
              <InfoField label={t('pending.field.street')} value={garage.streetAddress} />
              <InfoField label={t('pending.field.manager')} value={garage.managerName} />
              <InfoField label={t('pending.field.workers')} value={garage.numberOfWorkers?.toString()} />
            </div>
          )}
        </div>

        {garage.services.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '1rem' }}>
              {t('pending.services')} ({garage.services.length})
            </h2>
            {garage.services.map((s) => (
              <div key={s.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.75rem', background: '#f9fafb', borderRadius: 8, marginBottom: '0.5rem',
              }}>
                <div>
                  <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#1a1a2e' }}>{s.category.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.125rem' }}>
                    {s.durationMinutes > 0 && <span>{s.durationMinutes} {t('onboarding.services.min')}</span>}
                    {s.warrantyDescription && <span> &middot; {s.warrantyDescription}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1a1a2e' }}>AED {s.price.toFixed(2)}</div>
                  <div style={{
                    fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase',
                    color: s.status === 'ACTIVE' ? '#16a34a' : s.status === 'PENDING_APPROVAL' ? '#d97706' : '#6b7280',
                    marginTop: '0.125rem',
                  }}>
                    {t(`services.status.${s.status?.toLowerCase()}`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {garage.operatingHours.length > 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '1rem' }}>{t('pending.operatingHours')}</h2>
            {editMode ? (
              <table className="hours-table">
                <thead>
                  <tr><th>{t('pending.day')}</th><th>{t('onboarding.hours.open')}</th><th>{t('onboarding.hours.openTime')}</th><th>{t('onboarding.hours.closeTime')}</th></tr>
                </thead>
                <tbody>
                  {editHours.map((h) => (
                    <tr key={h.dayOfWeek}>
                      <td>{t(DAY_KEYS[isoToJsDow(h.dayOfWeek)]) || `Day ${h.dayOfWeek}`}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={!h.isClosed}
                          onChange={(e) => updateEditDay(h.dayOfWeek, { isClosed: !e.target.checked })}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          value={h.openTime}
                          disabled={h.isClosed}
                          onChange={(e) => updateEditDay(h.dayOfWeek, { openTime: e.target.value })}
                          style={{ padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.875rem' }}
                        />
                      </td>
                      <td>
                        <input
                          type="time"
                          value={h.closeTime}
                          disabled={h.isClosed}
                          onChange={(e) => updateEditDay(h.dayOfWeek, { closeTime: e.target.value })}
                          style={{ padding: '0.25rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.875rem' }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="hours-table">
                <thead>
                  <tr><th>{t('pending.day')}</th><th>{t('pending.status')}</th><th>{t('pending.hours')}</th></tr>
                </thead>
                <tbody>
                  {garage.operatingHours.map((h) => (
                    <tr key={h.id}>
                      <td>{t(DAY_KEYS[isoToJsDow(h.dayOfWeek)]) || `Day ${h.dayOfWeek}`}</td>
                      <td>{h.isClosed ? t('onboarding.hours.closed') : t('onboarding.hours.open')}</td>
                      <td>{h.isClosed ? '-' : `${h.openTime} - ${h.closeTime}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {(garage.media.length > 0 || documents.length > 0) && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '1rem' }}>{t('pending.uploadedFiles')}</h2>
            {garage.media.map((m) => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#f9fafb', borderRadius: 4, marginBottom: '0.5rem' }}>
                <div>
                  <span style={{ fontSize: '0.9rem', color: '#333' }}>{m.originalFilename}</span>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginInlineStart: '0.5rem' }}>({m.mediaType.replace('_', ' ')})</span>
                </div>
                {m.downloadUrl && (
                  <a href={m.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', fontSize: '0.85rem', textDecoration: 'none' }}>
                    {t('common.download')}
                  </a>
                )}
              </div>
            ))}
            {/* Documents from garage_documents (Emirates ID + others). Filter out
                TRADE_LICENSE rows because admin can have a synthesised one — the
                trade license file already appears above via GarageMedia. */}
            {documents
              .filter((d) => d.documentType !== 'TRADE_LICENSE')
              .map((d) => (
                <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#f9fafb', borderRadius: 4, marginBottom: '0.5rem' }}>
                  <div>
                    <span style={{ fontSize: '0.9rem', color: '#333' }}>{d.originalFilename || '—'}</span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginInlineStart: '0.5rem' }}>
                      ({d.documentType === 'OWNER_EMIRATES_ID' ? t('settings.ownerEmiratesId') : 'Other'})
                    </span>
                  </div>
                  {d.downloadUrl && (
                    <a href={d.downloadUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4f46e5', fontSize: '0.85rem', textDecoration: 'none' }}>
                      {t('common.download')}
                    </a>
                  )}
                </div>
              ))}
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1a1a2e', marginBottom: '1rem' }}>{t('pending.addMoreFiles')}</h2>
          <FileDropZone
            mediaType="OTHER_DOCUMENT"
            token={auth.token || ''}
            accept="image/jpeg,image/png,image/webp"
            multiple
            maxFiles={10}
            label={t('pending.addMoreFilesDrop')}
            files={[]}
            onUploadComplete={() => fetchGarage()}
          />
        </div>

        {editMode && (
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem', textAlign: 'center' }}>
            <button
              onClick={handleSubmitForReview}
              disabled={submitting || !form.name.trim() || !form.phone.trim()}
              style={{
                padding: '0.75rem 2rem',
                background: (!form.name.trim() || !form.phone.trim()) ? '#9ca3af' : '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: '1rem',
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? t('onboarding.terms.submitting') : t('pending.submitForReview')}
            </button>
            <p style={{ color: '#6b7280', fontSize: '0.8125rem', marginTop: '0.5rem' }}>
              {t('pending.submitForReview.note')}
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}

function InfoField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '0.9375rem', color: '#1a1a2e' }}>{value || '-'}</div>
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text' }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <div>
      <label style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 500, marginBottom: '0.25rem', display: 'block' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: 6,
          fontSize: '0.9375rem',
          color: '#1a1a2e',
          outline: 'none',
        }}
      />
    </div>
  );
}
